package com.zq.webproject.ui.register

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * 扫码注册页面 ViewModel
 */
class QRCodeRegisterViewModel {
    private val repository = Repository()
    private val scope = CoroutineScope(Dispatchers.Main + Job())
    
    // 表单字段
    var phone by mutableStateOf("")
        private set
    var password by mutableStateOf("")
        private set
    var code by mutableStateOf("")
        private set
    var invitationCodeDisplay by mutableStateOf("邀请码加载中...")
        private set
    
    private var actualInvitationCode: String = ""
    
    // 错误信息
    var phoneError by mutableStateOf("")
        private set
    var passwordError by mutableStateOf("")
        private set
    var codeError by mutableStateOf("")
        private set
    
    // 状态
    var isLoading by mutableStateOf(false)
        private set
    var errorMessage by mutableStateOf("")
        private set
    var successMessage by mutableStateOf("")
        private set
    var isRegisterSuccess by mutableStateOf(false)
        private set
    
    // 验证码相关
    var countdown by mutableStateOf(0)
        private set
    var isSendingCode by mutableStateOf(false)
        private set
    
    val canSendCode: Boolean
        get() = phone.isNotEmpty() && countdown == 0 && !isSendingCode
    
    val sendCodeButtonText: String
        get() = when {
            isSendingCode -> "发送中..."
            countdown > 0 -> "${countdown}s"
            else -> "获取验证码"
        }
    
    init {
        startCountdownTimer()
        
        // 初始化时尝试加载之前保存的邀请码（页面刷新后也能恢复）
        val savedInvitationCode = TokenManager.getPendingInvitationCode()
        if (!savedInvitationCode.isNullOrEmpty()) {
            actualInvitationCode = savedInvitationCode
            invitationCodeDisplay = savedInvitationCode
            println("QRCodeRegisterViewModel - 从本地存储恢复邀请码: $savedInvitationCode")
        }
    }
    
    /**
     * 设置邀请码（扫码进入时调用）
     * 同时保存到本地存储，防止页面刷新丢失
     */
    fun setInvitationCode(code: String) {
        actualInvitationCode = code
        invitationCodeDisplay = code
        // 保存到本地存储，防止页面刷新丢失
        TokenManager.savePendingInvitationCode(code)
        println("QRCodeRegisterViewModel - 邀请码已设置并持久化保存: $code")
    }
    
    /**
     * 更新手机号
     */
    fun updatePhone(value: String) {
        phone = value
        phoneError = ""
        errorMessage = ""
    }
    
    /**
     * 更新密码
     */
    fun updatePassword(value: String) {
        password = value
        passwordError = ""
        errorMessage = ""
    }
    
    /**
     * 更新验证码
     */
    fun updateCode(value: String) {
        code = value
        codeError = ""
        errorMessage = ""
    }
    
    /**
     * 发送验证码
     */
    fun sendVerificationCode() {
        println("QRCodeRegisterViewModel - 准备发送验证码")
        
        // 验证手机号
        if (phone.isBlank()) {
            phoneError = "请输入手机号"
            return
        }
        
        if (phone.length != 11) {
            phoneError = "手机号格式不正确"
            return
        }
        
        isSendingCode = true
        errorMessage = ""
        
        scope.launch {
            println("QRCodeRegisterViewModel - 开始发送验证码: $phone")
            
            val result = repository.sendCode(phone)
            
            if (result.isFailure) {
                val error = "发送失败: ${result.exceptionOrNull()?.message}"
                println("QRCodeRegisterViewModel - $error")
                errorMessage = error
                isSendingCode = false
                return@launch
            }
            
            val response = result.getOrNull()
            
            if (response?.code != 200) {
                val error = "发送失败: ${response?.message ?: "未知错误"}"
                println("QRCodeRegisterViewModel - $error")
                errorMessage = error
                isSendingCode = false
                return@launch
            }
            
            println("QRCodeRegisterViewModel - 验证码发送成功")
            countdown = 60
            isSendingCode = false
        }
    }
    
    /**
     * 注册
     */
    fun register() {
        println("QRCodeRegisterViewModel - 准备注册")
        
        // 重置错误信息
        phoneError = ""
        passwordError = ""
        codeError = ""
        errorMessage = ""
        successMessage = ""
        
        // 表单验证
        var hasError = false
        
        if (phone.isBlank()) {
            phoneError = "请输入手机号"
            hasError = true
        } else if (phone.length != 11) {
            phoneError = "手机号格式不正确"
            hasError = true
        }
        
        if (password.isBlank()) {
            passwordError = "请输入密码"
            hasError = true
        } else if (password.length < 6) {
            passwordError = "密码长度不能少于6位"
            hasError = true
        }
        
        if (code.isBlank()) {
            codeError = "请输入验证码"
            hasError = true
        }
        
        if (actualInvitationCode.isEmpty()) {
            errorMessage = "邀请码无效，请重新扫码"
            hasError = true
        }
        
        if (hasError) {
            println("QRCodeRegisterViewModel - 表单验证失败")
            return
        }
        
        isLoading = true
        
        scope.launch {
            println("QRCodeRegisterViewModel - 开始注册，手机号: $phone, 邀请码: $actualInvitationCode")
            
            // 获取设备信息用于注册
            val deviceId = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceId()
            } catch (e: Exception) {
                println("QRCodeRegisterViewModel - 获取设备ID失败: ${e.message}")
                errorMessage = "获取设备信息失败，无法注册"
                isLoading = false
                return@launch
            }
            
            val deviceName = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceName()
            } catch (e: Exception) {
                null
            }
            
            val deviceType = try {
                com.zq.webproject.utils.DeviceUtils.getDeviceType()
            } catch (e: Exception) {
                null
            }
            
            println("QRCodeRegisterViewModel - 设备信息: deviceId=$deviceId, deviceName=$deviceName, deviceType=$deviceType")
            
            // 调用注册接口（传递设备信息，注册时自动授权该设备）
            val registerResult = repository.register(phone, password, code, actualInvitationCode, deviceId, deviceName, deviceType)
            
            if (registerResult.isFailure) {
                val error = "注册失败: ${registerResult.exceptionOrNull()?.message}"
                println("QRCodeRegisterViewModel - $error")
                errorMessage = error
                isLoading = false
                return@launch
            }
            
            val registerResponse = registerResult.getOrNull()
            
            if (registerResponse?.code != 200) {
                val error = "注册失败: ${registerResponse?.message ?: "未知错误"}"
                println("QRCodeRegisterViewModel - $error")
                errorMessage = error
                isLoading = false
                return@launch
            }
            
            println("QRCodeRegisterViewModel - 注册成功！用户ID: ${registerResponse.data?.userId}，注册设备已自动授权")
            successMessage = "注册成功！正在自动登录..."
            
            // 短暂延迟，让用户看到成功消息
            delay(500)
            
            // 自动登录（使用相同的设备信息）
            println("QRCodeRegisterViewModel - 开始自动登录")
            
            val loginResult = repository.login(phone, password, deviceId, deviceName, deviceType)
            
            if (loginResult.isFailure) {
                val error = "登录失败: ${loginResult.exceptionOrNull()?.message}"
                println("QRCodeRegisterViewModel - $error")
                errorMessage = "注册成功但自动登录失败，请手动登录"
                isLoading = false
                return@launch
            }
            
            val loginResponse = loginResult.getOrNull()
            
            if (loginResponse?.code != 200 || loginResponse.data?.token == null) {
                // 特殊处理403错误（设备未授权）
                val error = if (loginResponse?.code == 403) {
                    "注册成功！但此设备未授权，无法自动登录。\n\n设备ID: ${deviceId.take(16)}...\n设备名称: ${deviceName ?: "未知"}\n设备类型: ${deviceType ?: "未知"}\n\n请联系管理员授权此设备后手动登录。"
                } else {
                    "登录失败: ${loginResponse?.message ?: "未知错误"}"
                }
                println("QRCodeRegisterViewModel - $error")
                errorMessage = error
                isLoading = false
                return@launch
            }
            
            println("QRCodeRegisterViewModel - 自动登录成功！Token: ${loginResponse.data.token}")
            successMessage = "注册成功！即将跳转..."
            
            // 清除待注册的邀请码（注册成功后不再需要）
            TokenManager.clearPendingInvitationCode()
            println("QRCodeRegisterViewModel - 已清除待注册邀请码")
            
            // 标记注册成功
            isRegisterSuccess = true
            isLoading = false
        }
    }
    
    /**
     * 启动倒计时定时器
     */
    private fun startCountdownTimer() {
        scope.launch {
            while (true) {
                delay(1000)
                if (countdown > 0) {
                    countdown--
                }
            }
        }
    }
}
